import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { Header } from './Dashboard';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [chatUser, setChatUser] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    const data = await api.get(`/admin/users?${params.toString()}`);
    setUsers(data.users);
    setPages(data.pages);
    setTotal(data.total);
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    await api.patch(`/admin/users/${id}`, patch);
    load();
  };

  return (
    <div>
      <Header title="Users" sub={`${total} registered users`} />

      <div style={styles.toolbar}>
        <input
          className="input"
          style={{ maxWidth: 280 }}
          placeholder="Search name or phone…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="input" style={{ maxWidth: 180 }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="incomplete">Incomplete</option>
          <option value="suspended">Suspended</option>
          <option value="blocked">Blocked</option>
        </select>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead>
            <tr>
              <th>User</th><th>Phone</th><th>Gender</th><th>Membership</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id}>
                <td>
                  <div style={styles.userCell}>
                    <img src={u.photos?.[0]?.url || 'https://via.placeholder.com/40'} alt="" style={styles.avatar} />
                    <div>
                      <div style={{ color: '#fff', fontWeight: 700 }}>
                        {u.name || '—'} {u.isVerified ? '✔️' : ''} {u.isFeatured ? '⭐' : ''}
                      </div>
                      <div style={{ fontSize: 12 }}>{u.age ? `${u.age} yrs` : ''}</div>
                    </div>
                  </div>
                </td>
                <td>+{u.phone}</td>
                <td style={{ textTransform: 'capitalize' }}>{u.gender || '—'}</td>
                <td><span className={`badge ${u.membership?.tier !== 'free' ? 'premium' : 'free'}`}>{u.membership?.tier || 'free'}</span></td>
                <td><span className={`badge ${u.status}`}>{u.status}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn sm" onClick={() => setDetailId(u._id)}>📋 Details</button>
                    <button className="btn sm" style={{ background: 'var(--secondary)' }} onClick={() => setChatUser(u)}>💬 Chats</button>
                    <button className="btn sm muted" onClick={() => update(u._id, { isVerified: !u.isVerified })}>{u.isVerified ? 'Unverify' : 'Verify'}</button>
                    <button className="btn sm muted" onClick={() => update(u._id, { isFeatured: !u.isFeatured })}>{u.isFeatured ? 'Unfeature' : 'Feature'}</button>
                    {u.status !== 'suspended' ? (
                      <button className="btn sm" style={{ background: 'var(--accent)', color: '#000' }} onClick={() => update(u._id, { status: 'suspended' })}>Suspend</button>
                    ) : (
                      <button className="btn sm green" onClick={() => update(u._id, { status: 'active' })}>Reactivate</button>
                    )}
                    {u.status !== 'blocked' ? (
                      <button className="btn sm danger" onClick={() => update(u._id, { status: 'blocked' })}>Block</button>
                    ) : (
                      <button className="btn sm green" onClick={() => update(u._id, { status: 'active' })}>Unblock</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>No users found.</td></tr> : null}
          </tbody>
        </table>
      </div>

      <div style={styles.pager}>
        <button className="btn ghost sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
        <span style={{ color: 'var(--text-dim)' }}>Page {page} of {pages}</span>
        <button className="btn ghost sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
      </div>

      {chatUser ? <ChatViewer user={chatUser} onClose={() => setChatUser(null)} /> : null}
      {detailId ? <UserDetail id={detailId} onClose={() => setDetailId(null)} onChanged={load} /> : null}
    </div>
  );
}

// Full user detail: membership/purchase, swipe stats, transactions, discontinue.
function UserDetail({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { setData(await api.get(`/admin/users/${id}`)); }, [id]);
  useEffect(() => { load(); }, [load]);

  const discontinue = async () => {
    if (!window.confirm('Discontinue this user\'s membership?')) return;
    await api.post(`/admin/users/${id}/discontinue`);
    await load();
    onChanged();
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div className="card" style={styles.detailModal} onClick={(e) => e.stopPropagation()}>
        {!data ? <div>Loading…</div> : (
          <>
            <div style={styles.chatHead}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src={data.user.photos?.[0]?.url || 'https://via.placeholder.com/48'} alt="" style={{ width: 48, height: 48, borderRadius: 24, objectFit: 'cover' }} />
                <div>
                  <strong style={{ fontSize: 17 }}>{data.user.name || '—'} {data.user.isVerified ? '✔️' : ''}</strong>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>+{data.user.phone || ''} {data.user.email ? `· ${data.user.email}` : ''}</div>
                </div>
              </div>
              <button className="btn ghost sm" onClick={onClose}>✕</button>
            </div>

            {/* Membership */}
            <div style={styles.memCard}>
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Membership</div>
                <div style={{ fontSize: 18, fontWeight: 800, textTransform: 'capitalize' }}>
                  {data.user.membership?.tier || 'free'} {data.isPaid ? <span className="badge premium">PAID</span> : <span className="badge free">FREE</span>}
                </div>
                {data.user.membership?.expiresAt ? <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Expires {new Date(data.user.membership.expiresAt).toLocaleDateString()}</div> : null}
              </div>
              {data.isPaid ? <button className="btn danger" onClick={discontinue}>Discontinue</button> : null}
            </div>

            {/* Swipe stats */}
            <div style={styles.statRow}>
              <Stat label="Right swipes" value={data.stats.rightSwipes} />
              <Stat label="Left swipes" value={data.stats.leftSwipes} />
              <Stat label="Super likes" value={data.stats.superLikes} />
              <Stat label="Likes received" value={data.stats.likesReceived} />
              <Stat label="Matches" value={data.stats.matches} />
            </div>

            {/* Transactions */}
            <h4 style={{ margin: '16px 0 8px' }}>Purchases ({data.transactions.length})</h4>
            <div style={styles.log} className="scrollbar">
              {data.transactions.length === 0 ? <div style={{ color: 'var(--text-muted)', padding: 10 }}>No purchases.</div> : null}
              {data.transactions.map((t) => (
                <div key={t._id} style={styles.txnRow}>
                  <div style={{ flex: 1 }}>{t.plan?.name || '—'} <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>· {t.gateway}</span></div>
                  <div style={{ color: 'var(--like)', fontWeight: 700 }}>₹{t.amountInr}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, width: 90, textAlign: 'right' }}>{new Date(t.createdAt).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={styles.stat}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)' }}>{value}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{label}</div>
    </div>
  );
}

// Admin chat moderation: a user's conversations on the left, messages on the right.
function ChatViewer({ user, onClose }) {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // { matchId, other }
  const [messages, setMessages] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${user._id}/conversations`).then((d) => setConvs(d.conversations));
  }, [user._id]);

  const open = async (c) => {
    setActive(c);
    const d = await api.get(`/admin/matches/${c.matchId}/messages`);
    setMessages(d.messages);
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div className="card" style={styles.chatModal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.chatHead}>
          <strong>💬 Chats — {user.name || user.phone}</strong>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>
        <div style={styles.chatBody}>
          <div style={styles.convList} className="scrollbar">
            {convs.length === 0 ? <div style={{ color: 'var(--text-muted)', padding: 14 }}>No conversations.</div> : null}
            {convs.map((c) => (
              <div key={c.matchId} onClick={() => open(c)} style={{ ...styles.convRow, ...(active?.matchId === c.matchId ? styles.convActive : {}) }}>
                <img src={c.other?.photos?.[0]?.url || 'https://via.placeholder.com/36'} alt="" style={styles.convAvatar} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontWeight: 700 }}>{c.other?.name || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.lastMessagePreview || `${c.messageCount} messages`}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={styles.msgPane} className="scrollbar">
            {!active ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: 40 }}>Select a conversation</div>
            ) : (
              <>
                <div style={styles.msgHead}>
                  <img src={active.other?.photos?.[0]?.url || 'https://via.placeholder.com/30'} alt="" style={styles.convAvatar} onClick={() => setProfile(active.other)} />
                  <button className="btn ghost sm" onClick={() => setProfile(active.other)}>View {active.other?.name}'s profile</button>
                </div>
                {messages.map((m) => {
                  const mine = String(m.sender) === String(user._id);
                  return (
                    <div key={m._id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
                      <div style={{ ...styles.bubble, ...(mine ? styles.bubbleMine : styles.bubbleTheirs) }}>
                        {m.type === 'text' ? m.text : `📷 ${m.type}`}
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 ? <div style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: 20 }}>No messages yet.</div> : null}
              </>
            )}
          </div>
        </div>
      </div>

      {profile ? (
        <div style={styles.backdrop} onClick={() => setProfile(null)}>
          <div className="card" style={styles.profileModal} onClick={(e) => e.stopPropagation()}>
            <img src={profile.photos?.[0]?.url || 'https://via.placeholder.com/120'} alt="" style={{ width: '100%', height: 260, objectFit: 'cover', borderRadius: 12 }} />
            <h2 style={{ margin: '14px 0 4px' }}>{profile.name || '—'}</h2>
            <div style={{ color: 'var(--text-dim)' }}>+{profile.phone || ''}</div>
            <button className="btn" style={{ marginTop: 16, width: '100%' }} onClick={() => setProfile(null)}>Close</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  toolbar: { display: 'flex', gap: 12, marginBottom: 16 },
  userCell: { display: 'flex', alignItems: 'center', gap: 12 },
  avatar: { width: 40, height: 40, borderRadius: 20, objectFit: 'cover', background: 'var(--card-alt)' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 18 },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, padding: 20 },
  chatModal: { width: 880, maxWidth: '95vw', height: '80vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' },
  chatHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid var(--border)' },
  chatBody: { display: 'flex', flex: 1, minHeight: 0 },
  convList: { width: 280, borderRight: '1px solid var(--border)', overflowY: 'auto' },
  convRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)' },
  convActive: { background: 'rgba(255,77,141,0.12)' },
  convAvatar: { width: 36, height: 36, borderRadius: 18, objectFit: 'cover', cursor: 'pointer' },
  msgPane: { flex: 1, overflowY: 'auto', padding: 18 },
  msgHead: { display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid var(--border)' },
  bubble: { maxWidth: '70%', padding: '9px 13px', borderRadius: 14, fontSize: 14 },
  bubbleMine: { background: 'var(--primary)', color: '#fff', borderBottomRightRadius: 4 },
  bubbleTheirs: { background: 'var(--card-alt)', color: 'var(--text)', borderBottomLeftRadius: 4 },
  profileModal: { width: 360, maxWidth: '90vw' },
  detailModal: { width: 600, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto' },
  memCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-alt)', borderRadius: 12, padding: 16, margin: '14px 0' },
  statRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 },
  stat: { background: 'var(--card-alt)', borderRadius: 10, padding: '12px 6px', textAlign: 'center' },
  log: { background: 'var(--bg-alt)', borderRadius: 10, maxHeight: 200, overflowY: 'auto' },
  txnRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: '1px solid var(--border)' },
};
